import { BrokerageAccountPosition } from '@/lib/api/risk/brokerage';
import moment from "moment-timezone";
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Shield } from 'lucide-react';
import { cn, createDateLabel, createDateLabelWith } from '@/lib/utils';
import { formatNumberToDisplay, safeParseDecimal } from '@/lib/api/common';

type ThreeWaySelectionState = 'hedgeable' | 'non_hedgeable' | 'dismiss' | null;

const getPAndSSubtypeLabel = (subtype: string | null | undefined): string => {
  if (subtype === 'p_and_s') return 'P&S';
  if (subtype === 'p_and_s_reversal') return 'P&S Rev';
  return '';
};

const getImportDate = (datetime: Date | string | null | undefined): string | null => {
  if (!datetime) return null;
  const date = typeof datetime === 'string' ? datetime.split("T")?.[0] : datetime.toISOString().split("T")?.[0];
  return moment(date, "YYYY-MM-DD").format("MM/DD/YYYY");
};

interface PositionCardProps {
  position: BrokerageAccountPosition;
  isStaged?: boolean;
  suggestionType?: 'add' | 'skip' | 'unknown';
  selectionState?: ThreeWaySelectionState;
  onSelectHedgeable?: () => void;
  onSelectNonHedgeable?: () => void;
  onSelectDismiss?: () => void;
  // Legacy props for backward compatibility
  onAccept?: () => void;
  onReject?: () => void;
  importDate?: string | Date | null;
}

export function PositionCard({ 
  position, 
  isStaged = false,
  suggestionType,
  selectionState,
  onSelectHedgeable,
  onSelectNonHedgeable,
  onSelectDismiss,
  // Legacy support
  onAccept,
  onReject,
  importDate: import_date
}: PositionCardProps) {
  const getBorderColor = () => {
    if (!isStaged) return 'border-gray-200';
    if (suggestionType === 'add') return 'border-green-500';
    if (suggestionType === 'skip') return 'border-yellow-500';
    if (suggestionType === 'unknown') return 'border-red-500';
    return 'border-gray-200';
  };

  const getAccentClass = () => {
    if (selectionState === 'hedgeable') return 'bg-green-50';
    if (selectionState === 'non_hedgeable') return 'bg-blue-50';
    if (selectionState === 'dismiss') return 'bg-red-50';
    return '';
  };

  return (
    <div className={cn(
      'border rounded-lg p-3 transition-colors',
      getBorderColor(),
      getAccentClass()
    )}>
      <div className="flex flex-col gap-1">
        <div className="flex items-start justify-between">
          <div className="flex flex-1 min-w-0 flex-col items-start md:justify-start md:flex-row md:items-center gap-2">
            <div className="font-semibold text-sm truncate flex flex-col md:flex-row">
              {position.trading_order_type === 'p_and_s' && position.trading_order_subtype && (
                <span className="inline-block w-[65px] text-muted-foreground mr-2">
                  [{getPAndSSubtypeLabel(position.trading_order_subtype)}]
                </span>
              )}
              {position.Customer?.long_name || position.customer_name || 'Unknown Customer'}
            </div>
            <div className="text-xs text-muted-foreground gap-2 truncate flex flex-col md:flex-row">
              {(position.commodity_label || position.commodity_type || '') && (<span className="text-md font-semibold">
                {(position.commodity_label || position.commodity_type) && " | "}
                <strong>{position.commodity_label || position.commodity_type}</strong>
                {position.crop_year && ` (${position.crop_year})`}
                </span>)}
                {position.trading_account && ` [${position.trading_account}]`}
                {position.Customer?.long_name && position.customer_name && ` | ${position.customer_name}`}
              {getImportDate(import_date) ? <span> - {getImportDate(import_date)}</span> : null}
            </div>
          </div>
          {isStaged && (onSelectHedgeable || onAccept) ? (
            <div className="flex gap-1 ml-2">
              {/* Three-way selection buttons */}
              {onSelectHedgeable && onSelectNonHedgeable && onSelectDismiss ? (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onSelectHedgeable}
                    className={cn(
                      "h-7 px-2",
                      selectionState === 'hedgeable' && "bg-green-100 text-green-700 hover:bg-green-200"
                    )}
                    title="Accept as Hedgeable"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onSelectNonHedgeable}
                    className={cn(
                      "h-7 px-2",
                      selectionState === 'non_hedgeable' && "bg-blue-100 text-blue-700 hover:bg-blue-200"
                    )}
                    title="Accept as Non-Hedgeable"
                  >
                    <Shield className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onSelectDismiss}
                    className={cn(
                      "h-7 px-2",
                      selectionState === 'dismiss' && "bg-red-100 text-red-700 hover:bg-red-200"
                    )}
                    title="Dismiss"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                /* Legacy two-button mode */
                onAccept && onReject && (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={onAccept}
                      className={cn(
                        "h-7 px-2",
                        selectionState === 'hedgeable' && "bg-green-100 text-green-700 hover:bg-green-200"
                      )}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={onReject}
                      className={cn(
                        "h-7 px-2",
                        selectionState === 'dismiss' && "bg-red-100 text-red-700 hover:bg-red-200"
                      )}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </>
                )
              )}
            </div>
          ) : (!isStaged && position.is_hedgeable !== undefined) ? (
            <div 
              className="flex items-center ml-2"
              title={position.is_hedgeable ? "Hedgeable" : "Non-Hedgeable"}
            >
              {position.is_hedgeable ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <Shield className="h-4 w-4 text-blue-600" />
              )}
            </div>
          ) : null}
        </div>

        <div className="text-sm flex flex-col md:flex-row gap-2 items-start md:items-center">
          <div className="flex gap-2 items-center text-sm text-muted-foreground">
            { position.long_qty > 0 && <span>{position.long_qty} Long {!/put|call/i.test(position.contract_description) ? getCallOrPutLabel(position.put_or_call) : ""}</span> }
            { position.short_qty > 0 && <span>{position.short_qty} Short {!/put|call/i.test(position.contract_description) ? getCallOrPutLabel(position.put_or_call) : ""}</span> }
            { position.option_month && <span >{position.option_month}</span> }
            {/* { (position.strike_price && !safeParseDecimal({})(position.strike_price).isZero()) && <span >{formatNumberToDisplay(safeParseDecimal({})(position.strike_price))}</span> } */}
          </div>
          <div className="text-sm text-muted-foreground truncate">
            {position.contract_description}
          </div>
          {position.trader_name && (
            <div className="text-sm text-muted-foreground">
              Trader: {position.trader_name}
            </div>
          )}
          { position.order_date?.length > 0 && (
            <span className="text-sm text-muted-foreground"> | {position.order_date.map((date) => createDateLabelWith({ field: typeof date === 'string' ? date : date?.toISOString() })).join(', ')}</span>
          )}
        </div>

        {position.trading_order_type === 'p_and_s' && (
          <div className="text-xs text-muted-foreground font-mono whitespace-pre block md:hidden">
            <div>
              | {(`$${formatNumberToDisplay(position.currency_adjusted_realized_amount)}`)}
              | {(`$${formatNumberToDisplay(position.original_realized_amount)}`)}
              | {(`$${formatNumberToDisplay(position.trade_price)}`)}
              | {(`$${formatNumberToDisplay(position.close_price)}`)}
            </div>
          </div>
        )}
        {position.trading_order_type === 'p_and_s' && (
          <div className="text-xs text-muted-foreground font-mono whitespace-pre hidden md:block">
            <div>|{["Adjusted USD", `Original ${position.original_currency_code}`, "Trade Price", "Close Price"].map(item => ` ${item}`.padEnd(17)).join("|")}|</div>
            <div>|-----------------|-----------------|-----------------|-----------------|</div>
            <div>
              | {(`$${formatNumberToDisplay(position.currency_adjusted_realized_amount)}`).padEnd(16)}
              | {(`$${formatNumberToDisplay(position.original_realized_amount)}`).padEnd(16)}
              | {(`$${formatNumberToDisplay(position.trade_price)}`).padEnd(16)}
              | {(`$${formatNumberToDisplay(position.close_price)}`).padEnd(16)}|
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function isCall(put_or_call?: string | null) {
  return put_or_call === 'C' || /call/i.test(put_or_call || "");
}

function isPut(put_or_call?: string | null) {
  return put_or_call === 'P' || /put/i.test(put_or_call || "");
}

function getCallOrPutLabel(put_or_call?: string | null) {
  if (!put_or_call) {
    return '';
  }

  if (isCall(put_or_call)) {
    return 'Call';
  }
  if (isPut(put_or_call)) {
    return 'Put';
  }

  return '';
}
